// /app/frontend/src/components/admin/fabric/TextureReplacePicker.jsx
// Dialog to pick a Publitas texture to replace a collection color's swatch image

import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Search, Loader2, CheckCircle2, Replace } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../ui/dialog';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export const TextureReplacePicker = ({ open, onOpenChange, color, collectionId, token, onReplaced }) => {
  const [search, setSearch] = useState('');
  const [textures, setTextures] = useState([]);
  const [loading, setLoading] = useState(false);
  const [replacing, setReplacing] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  const fetchTextures = useCallback(async (q) => {
    setLoading(true);
    try {
      const params = q ? { search: q } : {};
      const { data } = await axios.get(`${API}/api/admin/publitas-textures`, { headers, params });
      setTextures(data.textures || data);
    } catch {
      toast.error('Failed to load textures');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      // Pre-fill search with color name for best match
      const initial = color?.name || '';
      setSearch(initial);
      fetchTextures(initial);
      setSelectedId(null);
    }
  }, [open, color]);

  const handleSearch = () => {
    fetchTextures(search);
  };

  const handleReplace = async () => {
    if (!selectedId) return;
    setReplacing(true);
    try {
      const { data } = await axios.post(`${API}/api/admin/texture-replace`, {
        collection_id: collectionId,
        color_id: color.id,
        publitas_color_id: selectedId,
      }, { headers });
      toast.success(`Replaced with "${data.texture_name}" texture`);
      onReplaced?.(color.id, data.new_image_url);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to replace texture');
    } finally {
      setReplacing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col" data-testid="texture-replace-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Replace className="w-5 h-5 text-amber-600" />
            Replace Texture for "{color?.name}"
          </DialogTitle>
        </DialogHeader>

        {/* Search bar */}
        <div className="flex gap-2">
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
            placeholder="Search by name, code, or collection..."
            data-testid="texture-search-input"
          />
          <Button variant="outline" onClick={handleSearch} disabled={loading}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          </Button>
        </div>

        {/* Current vs New comparison */}
        {selectedId && (
          <div className="flex items-center gap-4 p-3 rounded-lg bg-amber-50 border border-amber-200">
            <div className="text-center">
              <p className="text-[10px] text-neutral-500 mb-1">Current</p>
              <div className="w-16 h-16 rounded-lg border-2 border-neutral-300 overflow-hidden bg-neutral-100"
                style={{ backgroundColor: color?.hex_color || '#ccc' }}>
                {color?.image_url && (
                  <img src={color.image_url} alt="current" className="w-full h-full object-cover" />
                )}
              </div>
            </div>
            <span className="text-neutral-400 text-lg">→</span>
            <div className="text-center">
              <p className="text-[10px] text-amber-600 mb-1 font-medium">New (Publitas)</p>
              <div className="w-16 h-16 rounded-lg border-2 border-amber-400 overflow-hidden bg-neutral-100">
                {(() => {
                  const sel = textures.find(t => t.id === selectedId);
                  const src = sel?.image_url || '';
                  return src ? <img src={src.startsWith('/') ? `${API}${src}` : src} alt="new" className="w-full h-full object-cover" /> : null;
                })()}
              </div>
            </div>
            <div className="ml-2 flex-1">
              <p className="text-sm font-medium text-amber-800">
                {textures.find(t => t.id === selectedId)?.name}
              </p>
              <p className="text-xs text-neutral-500">
                {textures.find(t => t.id === selectedId)?.code}
              </p>
            </div>
          </div>
        )}

        {/* Texture grid */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
            </div>
          ) : textures.length === 0 ? (
            <div className="text-center py-12 text-neutral-500 text-sm">
              No Publitas textures found. Sync a catalog first.
            </div>
          ) : (
            <div className="grid grid-cols-4 sm:grid-cols-5 lg:grid-cols-6 gap-2">
              {textures.map(tex => {
                const imgSrc = tex.image_url?.startsWith('/') ? `${API}${tex.image_url}` : tex.image_url;
                return (
                  <button
                    key={tex.id}
                    onClick={() => setSelectedId(tex.id === selectedId ? null : tex.id)}
                    className={`relative rounded-lg border-2 overflow-hidden transition-all text-left ${
                      selectedId === tex.id
                        ? 'border-amber-500 ring-2 ring-amber-300 scale-[1.02]'
                        : 'border-neutral-100 hover:border-neutral-300'
                    }`}
                    data-testid={`texture-option-${tex.id}`}
                  >
                    <div className="aspect-square bg-neutral-100 overflow-hidden">
                      <img src={imgSrc} alt={tex.name}
                        className="w-full h-full object-cover" loading="lazy"
                        onError={e => { e.target.style.display = 'none'; }} />
                    </div>
                    <div className="p-1.5">
                      <p className="text-[10px] font-medium text-neutral-800 truncate">{tex.name}</p>
                      <p className="text-[9px] text-neutral-400 truncate">{tex.brand_name}</p>
                    </div>
                    {selectedId === tex.id && (
                      <div className="absolute top-1 right-1">
                        <CheckCircle2 className="w-5 h-5 text-amber-500 bg-white rounded-full" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <DialogFooter className="mt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={handleReplace}
            disabled={!selectedId || replacing}
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="confirm-replace-btn"
          >
            {replacing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Replace className="w-4 h-4 mr-2" />}
            Replace Texture
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TextureReplacePicker;
